import {
  Button,
  Link,
  Table,
  TableSkeleton,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {MarketPlaceTrade} from '@/types/MarketPlace';

import {
  formatDecimal,
  getTradeDate,
  getTradePair,
  getTradeStatusClassName,
  getTradeStatusLabel,
} from './utils/format';

type TradeTableProps = {
  trades: MarketPlaceTrade[];
  isLoading: boolean;
  hasLoaded: boolean;
  /** A filter or an offer scope is narrowing the list. */
  hasActiveFilters: boolean;
  /** The error banner already explains the empty list; do not repeat it here. */
  hasError?: boolean;
  onSelect: (trade: MarketPlaceTrade) => void;
  onClearFilters: () => void;
};

const COLUMN_COUNT = 10;
// Td centres isNumeric and Th right-aligns it, so both cells spell the
// alignment out instead of relying on those two conflicting defaults.
const NUMERIC_CELL = 'text-right tabular-nums';

const TradeTable = ({
  trades,
  isLoading,
  hasLoaded,
  hasActiveFilters,
  hasError = false,
  onSelect,
  onClearFilters,
}: TradeTableProps) => {
  // Skeleton only for the first load; every later fetch keeps the current rows
  // on screen so the table does not jump between pages.
  if (isLoading && !hasLoaded) {
    return <TableSkeleton />;
  }

  const renderEmptyState = () => {
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center gap-2">
          <span>No trades match these filters</span>
          <Button variant="outline" colorScheme="cyan" onClick={onClearFilters}>
            Clear filters
          </Button>
        </div>
      );
    }
    return (
      <span>
        {/* Unfiltered, the server shows the last 30 days only. */}
        No trades in the last 30 days. A trade appears once a business bids on
        an offer and the bid is accepted.
      </span>
    );
  };

  return (
    <Table aria-label="Marketplace trades">
      <Thead>
        <Tr enableHover={false}>
          <Th className="ps-5">Trade</Th>
          <Th>Offer</Th>
          <Th>Counterparty</Th>
          <Th>Pair</Th>
          <Th className={NUMERIC_CELL}>Volume</Th>
          <Th className={NUMERIC_CELL}>Price</Th>
          <Th className={NUMERIC_CELL}>Counter amount</Th>
          <Th className="px-3">Status</Th>
          <Th>Placed</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <TBody>
        {trades.length === 0
          ? !hasError && (
              <Tr enableHover={false}>
                <Td
                  colSpan={COLUMN_COUNT}
                  className="py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  {renderEmptyState()}
                </Td>
              </Tr>
            )
          : trades.map((trade) => (
              <Tr key={trade.id}>
                <Td className="font-medium ps-5">#{trade.id}</Td>
                <Td>#{trade.askId}</Td>
                <Td>
                  <div className="flex flex-col">
                    <Link
                      to={`/merchants/businesses/${trade.businessId}`}
                      className="font-medium text-cyan-700 hover:underline dark:text-cyan-400"
                    >
                      {trade.businessName || trade.businessId}
                    </Link>
                    {trade.businessName && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {trade.businessId}
                      </span>
                    )}
                  </div>
                </Td>
                {/* The pair names both assets, so the numeric cells stay bare
                    and comparable instead of repeating "USDC" three times. */}
                <Td>{getTradePair(trade)}</Td>
                <Td className={NUMERIC_CELL}>
                  {formatDecimal(trade.volume.value)}
                </Td>
                <Td className={NUMERIC_CELL}>
                  {formatDecimal(trade.price.value)}
                </Td>
                <Td className={NUMERIC_CELL}>
                  {formatDecimal(trade.counterAmount.value)}
                </Td>
                <Td className="px-3">
                  <div className="flex flex-col">
                    <span
                      className={`font-medium ${getTradeStatusClassName(
                        trade.status,
                      )}`}
                    >
                      {getTradeStatusLabel(trade.status)}
                    </span>
                    {trade.paidAt && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Paid {getTradeDate(trade.paidAt)}
                      </span>
                    )}
                  </div>
                </Td>
                <Td>{getTradeDate(trade.createdAt)}</Td>
                <Td>
                  <Button
                    variant="outline"
                    colorScheme="cyan"
                    onClick={() => onSelect(trade)}
                  >
                    View
                  </Button>
                </Td>
              </Tr>
            ))}
      </TBody>
    </Table>
  );
};

export default TradeTable;
